import { ApolloClient, InMemoryCache, from } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import { RetryLink } from '@apollo/client/link/retry';
import { BiscuitsError } from '@biscuits/error';
import * as CONFIG from './config.js';
import { fetch, refreshSession } from './fetch.js';
let hasNetworkError = false;
function createErrorHandler(onError, onLoggedOut) {
    return ({ graphQLErrors, networkError, operation, forward }) => {
        var _a;
        let errorMessage = 'An unexpected error occurred. Please try again.';
        if (graphQLErrors) {
            for (const err of graphQLErrors) {
                console.error('Error during op', operation.operationName, err);
                if ((_a = err.extensions) === null || _a === void 0 ? void 0 : _a.biscuitsCode) {
                    const code = err.extensions.biscuitsCode;
                    if (code >= 4010 &&
                        code < 4020 &&
                        code !== BiscuitsError.Code.SessionExpired) {
                        errorMessage = undefined;
                        operation.setContext(async () => {
                            // attempt to refresh the session
                            console.log('Attempting to refresh session');
                            const success = await refreshSession(`${CONFIG.API_ORIGIN}/auth/refresh`);
                            console.log('Refresh session succeeded:', success);
                            if (success) {
                                // retry the original request
                                return forward(operation);
                            }
                            else {
                                console.error('Failed to refresh session');
                                // failed to refresh the session - the user needs
                                // to log in to use this query.
                                onLoggedOut === null || onLoggedOut === void 0 ? void 0 : onLoggedOut();
                                return;
                            }
                        });
                    }
                    else {
                        errorMessage = err.message;
                    }
                }
            }
        }
        else if (networkError) {
            if ('statusCode' in networkError && networkError.statusCode === 401) {
                errorMessage = undefined;
                operation.setContext(async () => {
                    // attempt to refresh the session
                    console.log('Attempting to refresh session');
                    const success = await refreshSession(CONFIG.API_ORIGIN);
                    console.log('Refresh session succeeded:', success);
                    if (success) {
                        // retry the original request
                        return forward(operation);
                    }
                    else {
                        console.error('Failed to refresh session');
                        // failed to refresh the session - the user needs
                        // to log in to use this query.
                        onLoggedOut === null || onLoggedOut === void 0 ? void 0 : onLoggedOut();
                        return;
                    }
                });
            }
            else if (!hasNetworkError) {
                hasNetworkError = true;
                console.error(networkError);
                if (networkError.message === 'Failed to fetch') {
                    // errorMessage =
                    //   'Having trouble reaching Biscuits servers. Running in offline mode.';
                    // this is communicated through UserMenu
                    errorMessage = undefined;
                }
                else {
                    errorMessage =
                        'A network error occurred. Please check your connection.';
                }
            }
            else {
                errorMessage = undefined;
            }
        }
        if (!networkError) {
            hasNetworkError = false;
        }
        if (errorMessage && !operation.getContext().hideErrors)
            onError === null || onError === void 0 ? void 0 : onError(errorMessage);
    };
}
const createHttp = (origin) => new HttpLink({
    fetch,
    credentials: 'include',
    uri: `${origin}/graphql`,
});
const retry = new RetryLink({
    delay: {
        initial: 500,
        max: Infinity,
        jitter: true,
    },
    attempts: {
        max: 10,
        retryIf: (error, _operation) => {
            if (error.networkError)
                return true;
            return false;
        },
    },
});
export function createGraphQLClient({ origin = CONFIG.API_ORIGIN, onError: errorHandler, onLoggedOut, } = {}) {
    const http = createHttp(origin);
    return new ApolloClient({
        uri: `${origin}/graphql`,
        cache: new InMemoryCache({}),
        link: from([
            onError(createErrorHandler(deduplicateErrors(errorHandler), onLoggedOut)),
            retry,
            http,
        ]),
    });
}
export const defaultErrorHandlerRef = {
    onError: (error) => {
        console.error(error);
    },
};
export const graphqlClient = createGraphQLClient(defaultErrorHandlerRef);
function deduplicateErrors(onError) {
    if (!onError)
        return undefined;
    // only show 1 of each error message within a time window
    const errors = new Set();
    return (error) => {
        if (errors.has(error))
            return;
        errors.add(error);
        setTimeout(() => {
            errors.delete(error);
        }, 5000);
        onError(error);
    };
}
export function createMinimalGraphQLClient({ origin = CONFIG.API_ORIGIN, onError: errorHandler, onLoggedOut, } = {}) {
    const http = createHttp(origin);
    return new ApolloClient({
        cache: new InMemoryCache(),
        link: from([
            onError(createErrorHandler(deduplicateErrors(errorHandler), onLoggedOut)),
            http,
        ]),
    });
}
export { ApolloError, ApolloProvider, NetworkStatus, isApolloError as isClientError, useBackgroundQuery, useApolloClient as useClient, useFragment, useLazyQuery, useLoadableQuery, useMutation, useQuery, useReactiveVar, useReadQuery, useSubscription, useSuspenseQuery, } from '@apollo/client';
//# sourceMappingURL=graphql.js.map